import React, { ReactNode } from 'react';
import { motion } from 'framer-motion';

interface CircularTextProps {
  text: string;
  children: ReactNode; // Center element
  radius?: number;
  isHovered?: boolean; // New prop for hover state
}

const CircularText: React.FC<CircularTextProps> = ({
  text,
  children,
  radius = 100, // Adjust radius as needed
  isHovered = false, // Default to false
}) => {
  const circumference = 2 * Math.PI * radius;
  const id = `circular-text-path-${React.useId()}`;
  const viewBoxSize = radius * 2 + 20; // Add padding

  // Determine text color class based on hover state
  const textColorClass = isHovered
    ? 'fill-zinc-100 dark:fill-zinc-900' // Hover state SVG fill colors
    : 'fill-zinc-600 dark:fill-zinc-400'; // Default state SVG fill colors

  return (
    // Removed className application here, controlled within SVG now
    <div
      className="relative flex justify-center items-center"
      style={{ width: viewBoxSize, height: viewBoxSize }}
    >
      {/* Center Element */}
      <div className="absolute">{children}</div>

      {/* SVG for Circular Text */}
      <motion.svg
        viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`}
        width={viewBoxSize}
        height={viewBoxSize}
        className="absolute overflow-visible"
        key={isHovered ? 'hovered' : 'rest'}
        initial={{ rotate: 0 }}
        animate={{ rotate: 360 }}
        transition={{ repeat: Infinity, repeatType: 'loop', ease: 'linear', duration: isHovered ? 10 : 18 }}
      >
        <defs>
          <path
            id={id}
            d={`
              M ${viewBoxSize / 2}, ${viewBoxSize / 2 - radius}
              a ${radius},${radius} 0 1,1 0,${radius * 2}
              a ${radius},${radius} 0 1,1 0,-${radius * 2}
            `}
            fill="none"
          />
        </defs>
        <text
          dy="-4" // Adjust vertical position if needed
          textLength={circumference * 0.9}
          lengthAdjust="spacingAndGlyphs"
          // Apply font class directly to the text element
          className="font-konexy"
        >
          {/* Apply conditional fill color class and base styles here */}
          <textPath
            href={`#${id}`}
            startOffset="0%"
            className={`text-xs uppercase tracking-wider transition-colors duration-300 ${textColorClass}`}
          >
            {text}
          </textPath>
        </text>
      </motion.svg>
    </div>
  );
};

export default CircularText;
